import { useEffect, useState, useMemo } from "react";
import { getToken, getUserId } from "../utils/auth";
import "./StudentLeetCodeList.css";

export default function StudentLeetCodeList() {
  const [profiles, setProfiles] = useState([]);
  // eslint-disable-next-line
  const [staff, setStaff] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [sortConfig, setSortConfig] = useState({ key: "total", direction: "desc" });

  const userId = getUserId();
  const apiBaseUrl = process.env.REACT_APP_API_BASE_URL;

  const getCountByDifficulty = (array, difficulty) => {
    if (!Array.isArray(array)) return 0;
    const found = array.find((item) => item?.difficulty === difficulty);
    return found ? found.count || 0 : 0;
  };

  useEffect(() => {
    async function fetchLeetCodeData() {
      try {
        setError(null);
        setLoading(true);

        const staffRes = await fetch(`${apiBaseUrl}/api/getStaffById/${userId}`, {
          headers: { Authorization: `Bearer ${getToken()}` },
        });

        if (!staffRes.ok) throw new Error("Failed to fetch staff information.");
        const staffData = await staffRes.json();
        setStaff(staffData);

        // Determine target colleges array
        const targetColleges =
          Array.isArray(staffData?.colleges) && staffData.colleges.length > 0
            ? staffData.colleges
            : staffData?.college
            ? [staffData.college]
            : [];

        if (targetColleges.length === 0) {
          setProfiles([]);
          return;
        }

        // Fetch profile data across all target colleges in parallel
        const profilePromises = targetColleges.map(async (collegeName) => {
          try {
            const res = await fetch(`${apiBaseUrl}/api/student/get-leetcode-profile-by-college`, {
              method: "POST",
              headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${getToken()}`,
              },
              body: JSON.stringify({ college: collegeName }),
            });

            if (!res.ok) throw new Error(`Failed for ${collegeName}`);
            const data = await res.json();
            const studentList = data?.students || (Array.isArray(data) ? data : []);

            return studentList.map((student) => ({
              ...student,
              college: student?.college || student?.collegeName || collegeName,
            }));
          } catch (err) {
            console.error(`Error fetching for college ${collegeName}:`, err);
            return [];
          }
        });

        const results = await Promise.all(profilePromises);
        const rawStudents = results.flat();

        // Deduplicate students
        const uniqueStudentsMap = new Map();
        rawStudents.forEach((student) => {
          const id = student?._id || student?.studentId || student?.username;
          if (id && !uniqueStudentsMap.has(id)) {
            uniqueStudentsMap.set(id, student);
          }
        });

        const combinedStudents = Array.from(uniqueStudentsMap.values());

        const processedStudents = combinedStudents.map((student) => {
          const accepted = student?.profile?.numAcceptedQuestions || [];
          const easy = getCountByDifficulty(accepted, "EASY");
          const medium = getCountByDifficulty(accepted, "MEDIUM");
          const hard = getCountByDifficulty(accepted, "HARD");
          return {
            ...student,
            metrics: { easy, medium, hard, total: easy + medium + hard },
          };
        });

        setProfiles(processedStudents);
      } catch (err) {
        console.error("Error fetching LeetCode metrics:", err);
        setError(err.message || "Something went wrong while retrieving data.");
      } finally {
        setLoading(false);
      }
    }

    if (userId) fetchLeetCodeData();
  }, [userId, apiBaseUrl]);

  const requestSort = (key) => {
    let direction = "desc";
    if (sortConfig.key === key && sortConfig.direction === "desc") {
      direction = "asc";
    }
    setSortConfig({ key, direction });
  };

  const { filteredAndSortedProfiles, summaryStats } = useMemo(() => {
    let result = profiles.filter((student) => {
      const matchString = `${student?.name || ""} ${student?.email || ""} ${student?.username || ""} ${student?.college || ""}`.toLowerCase();
      return matchString.includes(searchTerm.toLowerCase().trim());
    });

    result.sort((a, b) => {
      let aVal, bVal;

      if (["easy", "medium", "hard", "total"].includes(sortConfig.key)) {
        aVal = a.metrics?.[sortConfig.key] ?? 0;
        bVal = b.metrics?.[sortConfig.key] ?? 0;
      } else if (sortConfig.key === "beats") {
        aVal = a.profile?.totalQuestionBeatsPercentage ?? 0;
        bVal = b.profile?.totalQuestionBeatsPercentage ?? 0;
      } else {
        aVal = (a[sortConfig.key] || "").toString().toLowerCase();
        bVal = (b[sortConfig.key] || "").toString().toLowerCase();
      }

      if (aVal < bVal) return sortConfig.direction === "asc" ? -1 : 1;
      if (aVal > bVal) return sortConfig.direction === "asc" ? 1 : -1;
      return 0;
    });

    const totalSolvedByAll = profiles.reduce((sum, s) => sum + (s.metrics?.total || 0), 0);
    const avgSolved = profiles.length ? Math.round(totalSolvedByAll / profiles.length) : 0;
    
    // Find top performer based on total solved questions
    const topPerformerObj = profiles.length 
      ? [...profiles].sort((a, b) => (b.metrics?.total || 0) - (a.metrics?.total || 0))[0] 
      : null;

    return {
      filteredAndSortedProfiles: result,
      summaryStats: { 
        avgSolved, 
        totalSolvedByAll, 
        topPerformer: topPerformerObj?.name || "N/A" 
      },
    };
  }, [profiles, searchTerm, sortConfig]);

  if (loading) {
    return (
      <div className="state-container">
        <div className="loading-spinner"></div>
        <p className="state-text">Assembling campus leaderboard metrics...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="state-container error-state">
        <div className="error-icon">⚠️</div>
        <p className="error-text">{error}</p>
        <button onClick={() => window.location.reload()} className="retry-btn">
          Reload Dashboard
        </button>
      </div>
    );
  }

  return (
    <div className="leetcode-page">
      {/* Dashboard Header */}
      <header className="page-header">
        <div className="header-content">
          <div>
            <h1 className="page-title">LeetCode Leaderboard</h1>
            <p className="page-subtitle">Track competitive programming performance and metrics across engineering cohorts.</p>
          </div>
          <div className="badge-count">
            <span className="pulse-indicator"></span>
            {profiles.length} Active Candidates
          </div>
        </div>
      </header>

      {/* Analytics Cards */}
      {profiles.length > 0 && (
        <section className="stats-grid">
          <div className="stat-card">
            <div className="stat-icon-wrapper icon-avg">📊</div>
            <div className="stat-details">
              <span className="stat-label">Campus Average</span>
              <span className="stat-value">{summaryStats.avgSolved.toLocaleString()}</span>
            </div>
          </div>
          <div className="stat-card">
            <div className="stat-icon-wrapper icon-total">⚡</div>
            <div className="stat-details">
              <span className="stat-label">Combined Solutions</span>
              <span className="stat-value text-total">{summaryStats.totalSolvedByAll.toLocaleString()}</span>
            </div>
          </div>
          <div className="stat-card top-performer-card">
            <div className="stat-icon-wrapper icon-mvp">👑</div>
            <div className="stat-details">
              <span className="stat-label">MVP Performer</span>
              <span className="stat-value text-mvp">
                {summaryStats.topPerformer !== "N/A" ? summaryStats.topPerformer.toUpperCase() : "N/A"}
              </span>
            </div>
          </div>
        </section>
      )}

      {/* Control Panel */}
      <div className="dashboard-controls">
        <div className="search-wrapper">
          <span className="search-icon">🔍</span>
          <input
            type="text"
            className="search-input"
            placeholder="Filter profiles by student name, email, college, or handle..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
          {searchTerm && (
            <button className="clear-search" onClick={() => setSearchTerm("")}>
              ✕
            </button>
          )}
        </div>
      </div>

      {/* Main Leaderboard Table */}
      {filteredAndSortedProfiles.length === 0 ? (
        <div className="empty-state">
          <div className="empty-icon">📂</div>
          <p className="empty-title">No Profiles Found</p>
          <p className="empty-desc">No matching student metrics were found in this view context.</p>
        </div>
      ) : (
        <div className="table-container">
          <table className="leetcode-table">
            <thead>
              <tr>
                <th onClick={() => requestSort("name")} className="sortable-th">
                  <div className="th-content">
                    Rank & Student Info
                    <span className="sort-indicator">{sortConfig.key === "name" ? (sortConfig.direction === "asc" ? " ▲" : " ▼") : " ↕"}</span>
                  </div>
                </th>
                <th onClick={() => requestSort("college")} className="sortable-th">
                  <div className="th-content">
                    College
                    <span className="sort-indicator">{sortConfig.key === "college" ? (sortConfig.direction === "asc" ? " ▲" : " ▼") : " ↕"}</span>
                  </div>
                </th>
                <th>Academic Track</th>
                <th>LeetCode Handle</th>
                <th onClick={() => requestSort("easy")} className="text-center sortable-th text-easy">
                  <div className="th-content text-center">
                    Easy
                    <span className="sort-indicator">{sortConfig.key === "easy" ? (sortConfig.direction === "asc" ? " ▲" : " ▼") : " ↕"}</span>
                  </div>
                </th>
                <th onClick={() => requestSort("medium")} className="text-center sortable-th text-medium">
                  <div className="th-content text-center">
                    Medium
                    <span className="sort-indicator">{sortConfig.key === "medium" ? (sortConfig.direction === "asc" ? " ▲" : " ▼") : " ↕"}</span>
                  </div>
                </th>
                <th onClick={() => requestSort("hard")} className="text-center sortable-th text-hard">
                  <div className="th-content text-center">
                    Hard
                    <span className="sort-indicator">{sortConfig.key === "hard" ? (sortConfig.direction === "asc" ? " ▲" : " ▼") : " ↕"}</span>
                  </div>
                </th>
                <th onClick={() => requestSort("total")} className="text-center sortable-th text-total">
                  <div className="th-content text-center">
                    Total Solved
                    <span className="sort-indicator">{sortConfig.key === "total" ? (sortConfig.direction === "asc" ? " ▲" : " ▼") : " ↕"}</span>
                  </div>
                </th>
                <th onClick={() => requestSort("beats")} className="text-center sortable-th">
                  <div className="th-content text-center">
                    Global Beats %
                    <span className="sort-indicator">{sortConfig.key === "beats" ? (sortConfig.direction === "asc" ? " ▲" : " ▼") : " ↕"}</span>
                  </div>
                </th>
              </tr>
            </thead>
            <tbody>
              {filteredAndSortedProfiles.map((student, index) => {
                const { easy = 0, medium = 0, hard = 0, total = 0 } = student.metrics || {};
                const rankNumber = index + 1;

                return (
                  <tr key={student._id || student.studentId || index} className={`rank-row row-rank-${rankNumber}`}>
                    <td>
                      <div className="student-meta">
                        <span className={`rank-badge ${rankNumber <= 3 ? `rank-${rankNumber}` : "rank-default"}`}>
                          {rankNumber}
                        </span>
                        <div className="student-info">
                          <span className="student-name">
                            {student.name ? student.name.toUpperCase() : "UNNAMED STUDENT"}
                          </span>
                          <span className="student-email">{student.email || "No email provided"}</span>
                        </div>
                      </div>
                    </td>
                    <td>
                      <span className="college-badge">{student.college || "N/A"}</span>
                    </td>
                    <td>
                      <div className="academic-meta">
                        {student.year && <span className="year-tag">Year {student.year}</span>}
                        {student.batch && <span className="batch-badge">{student.batch}</span>}
                        {!student.year && !student.batch && <span className="text-muted">—</span>}
                      </div>
                    </td>
                    <td>
                      {student.username ? (
                        <a
                          href={student.leetcodeURL || `https://leetcode.com/u/${student.username}/`}
                          target="_blank"
                          rel="noreferrer"
                          className="leetcode-link"
                        >
                          @{student.username}
                          <span className="link-arrow">↗</span>
                        </a>
                      ) : (
                        <span className="text-muted">Unlinked</span>
                      )}
                    </td>
                    <td className="text-center val-easy font-mono">{easy}</td>
                    <td className="text-center val-medium font-mono">{medium}</td>
                    <td className="text-center val-hard font-mono">{hard}</td>
                    <td className="text-center val-total font-mono">{total}</td>
                    <td className="text-center val-percent font-mono">
                      {student.profile?.totalQuestionBeatsPercentage != null
                        ? `${Number(student.profile.totalQuestionBeatsPercentage).toFixed(1)}%`
                        : "—"}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}